import { fetchSvgContentWithCache, image2Base64, loadImageAsync } from '@/utils/file-kit';
import type {
    TextRenderArtText,
    TextRenderGraphicListMarker,
    TextRenderHighlight,
    TextRenderInput,
    TextRenderTexture,
} from '../types';
import type {
    AssetGetters,
    AssetLoadState,
    ReadyTextRenderArtText,
    ReadyTextRenderAsset,
    ReadyTextRenderHighlight,
    ReadyTextRenderListMarker,
    ReadyTextRenderTexture,
} from './types';

type AssetRequest =
    | { kind: 'texture'; source: TextRenderTexture }
    | { kind: 'highlight'; source: TextRenderHighlight }
    | { kind: 'art-text'; source: TextRenderArtText }
    | { kind: 'list-marker'; source: TextRenderGraphicListMarker };

type AssetEntry = {
    request: AssetRequest;
    status: 'loading' | 'ready' | 'failed';
    asset?: ReadyTextRenderAsset;
    error?: Error;
    promise?: Promise<void>;
};

/**
 * 补充解释：为什么 AssetEntry 中用 loading，而 AssetLoadState 中用 pending？
 *
 * loading 描述单个素材正在执行加载任务；pending 描述当前输入还无法获得最终结果。
 * 只要任一 AssetEntry 仍处于 loading，整批素材的 AssetLoadState 就是 pending。
 */

type AssetLoaderOptions = {
    /**
     * 设置当前图层是否处于素材加载中。
     */
    setLoading: (loading: boolean) => void;

    /**
     * 通知宿主当前 Renderer 结果已失效，需使用最新输入重新渲染。
     */
    notifyInvalidated: () => void;
};

/**
 * 通过现有图片系统边界加载可嵌入文字 SVG 的图片。
 *
 * @param url - 已由宿主归一化的图片 URL。
 * @returns 可安全嵌入 SVG 的 data URL 与图片自然尺寸。
 */
const loadAssetImage = async (url: string) => {
    const image = await loadImageAsync(url, false);
    const loadedURL = image.currentSrc || image.src;
    return {
        dataURL: loadedURL.startsWith('data:image/') ? loadedURL : image2Base64(image),
        naturalWidth: image.naturalWidth || image.width,
        naturalHeight: image.naturalHeight || image.height,
    };
};

/**
 * 从按渲染能力组织的公开输入收集内部加载请求。
 *
 * @param input - 当前 Text Render Input。
 * @returns 当前输入实际使用的全部素材请求。
 */
const collectAssetRequests = (input: TextRenderInput): AssetRequest[] => {
    const requests: AssetRequest[] = [];

    if (input.texture) requests.push({ kind: 'texture', source: input.texture });

    const highlights = [input.highlight, ...input.spans.map((span) => span.highlight)].filter(
        (highlight): highlight is TextRenderHighlight => highlight != null,
    );
    highlights.forEach((source) => {
        if (source.config.src.trim()) requests.push({ kind: 'highlight', source });
    });

    if (input.artText) requests.push({ kind: 'art-text', source: input.artText });

    // 只有图形型 marker 需要加载素材；字符型 marker 由文字引擎塑形，不产生素材请求。
    if (input.listMarker && input.listMarker.kind !== 'character') {
        requests.push({ kind: 'list-marker', source: input.listMarker });
    }

    return requests;
};

/**
 * 生成只代表网络加载身份的素材缓存键。
 *
 * 尺寸、颜色、stretch 等绘制配置由当前输入同步消费，不应因其变化重复下载同一素材。
 *
 * @param request - Renderer 内部素材请求。
 * @returns renderer 内稳定的素材缓存键。
 */
const createAssetKey = (request: AssetRequest): string => {
    if (request.kind === 'texture') return JSON.stringify([request.kind, request.source.url]);
    if (request.kind === 'list-marker') {
        return JSON.stringify([request.kind, request.source.kind, request.source.url]);
    }
    if (request.kind === 'highlight') return JSON.stringify([request.kind, request.source.config.src.trim()]);
    return JSON.stringify([request.kind, request.source.url]);
};

/**
 * 加载一个 Renderer 内部素材请求。
 *
 * @param request - 从当前输入收集的素材请求。
 * @returns 同步 render pipeline 可读取的 ready 素材。
 */
const loadAsset = async (request: AssetRequest): Promise<ReadyTextRenderAsset> => {
    if (request.kind === 'texture') {
        const image = await loadAssetImage(request.source.url);
        return { kind: 'texture', ...image };
    }
    if (request.kind === 'list-marker') {
        if (request.source.kind === 'image') {
            const image = await loadAssetImage(request.source.url);
            return { kind: 'list-marker', type: 'image', dataURL: image.dataURL };
        }
        const svgText = await fetchSvgContentWithCache(request.source.url);
        return { kind: 'list-marker', type: 'svg', svgText };
    }
    if (request.kind === 'highlight') {
        const svgText = await fetchSvgContentWithCache(request.source.config.src.trim());
        return { kind: 'highlight', svgText };
    }
    const svgText = await fetchSvgContentWithCache(request.source.url);
    return { kind: 'art-text', svgText };
};

/**
 * 管理单个 Text Renderer 声明素材的缓存与 latest-only readiness。
 */
export class AssetLoader {
    /**
     * 当前同步渲染输入正在使用的素材 key。
     *
     * 只由 `ensure()` 整体替换；只有仍在这个集合中的素材加载完成后，才需要结束 loading 并通知宿主。
     * `prepare()` 是只读等待，不写入该集合，因此不会影响同步渲染的 loading 与失效通知。
     */
    private activeKeys = new Set<string>();

    /**
     * 已发起过的素材请求缓存。
     *
     * 每个 key 保存已发起的加载任务及其成功或失败结果。
     */
    private entries = new Map<string, AssetEntry>();

    /**
     * 创建 renderer 私有的素材 loader。
     *
     * @param options - Loader 使用的宿主能力。
     * @returns AssetLoader 实例。
     */
    constructor(private readonly options: AssetLoaderOptions) {}

    /**
     * 同步检查当前输入声明的全部素材。
     *
     * 该方法用于 render 同步路径。它会把当前输入记录为最新输入，
     * 复用已有缓存，并为未加载的素材立即发起请求，但不会等待请求完成。
     *
     * 只要仍有素材在加载就返回 pending。最新输入的全部素材进入 ready 或 failed 后，
     * Loader 会结束 loading 并通知宿主使用最新数据重新调用渲染。已被新输入取代的请求
     * 完成时不会通知宿主。加载失败会保留在缓存中，同一素材不会自动重试。
     *
     * @param input - 当前 Text Render Input。
     * @returns 全部素材可用时返回 ready assets，加载中返回 pending，任一素材失败时返回 failed。
     */
    ensure(input: TextRenderInput): AssetLoadState {
        return this.resolve(input);
    }

    /**
     * 以只读方式等待指定输入声明的全部素材进入终态，供导出路径使用。
     *
     * 复用已有缓存，为未加载的素材发起请求并等待完成，但不写入 `activeKeys`，也不上报 loading、
     * 不发布失效通知。
     *
     * 若同步渲染也在等待同一素材，保留其 loading 结束与失效通知；同步路径可复用本次创建的缓存。
     *
     * 加载失败会返回 failed 并保留在缓存中，同一素材不会自动重试。
     *
     * @param input - 需要准备素材的 Text Render Input。
     * @returns 全部素材可用时返回 ready assets；任一素材失败时返回 failed；等待期间 loader 被释放时返回 pending。
     */
    async prepare(input: TextRenderInput): Promise<AssetLoadState> {
        const requests = collectAssetRequests(input);
        const entries = requests.map((request) => this.getOrCreateEntry(request));

        await Promise.all(entries.map((entry) => entry.promise));

        let failure: Error | null = null;
        for (const [index, entry] of entries.entries()) {
            // dispose() 清空缓存后，loadEntry 不再回写本次 entry，它会停留在 loading。
            if (entry.status === 'loading') return { status: 'pending' };
            if (entry.status === 'failed') {
                failure ??= entry.error ?? new Error(`Failed to load ${requests[index]!.kind} asset.`);
            }
        }

        if (failure) return { status: 'failed', error: failure };
        return { status: 'ready', assets: this.createAssetGetters() };
    }

    /**
     * 清空当前活跃素材集合，并阻止已经过期的请求发布失效通知。
     *
     * @returns void。
     */
    reset(): void {
        // 在清空 activeKeys 前记录加载状态，确保 reset 能结束宿主侧的 loading 生命周期。
        const wasLoading = this.hasActiveLoading();
        this.activeKeys.clear();
        if (wasLoading) this.options.setLoading(false);
    }

    /**
     * 释放缓存，并阻止所有在途素材请求发布失效通知。
     *
     * @returns void。
     */
    dispose(): void {
        this.reset();
        this.entries.clear();
    }

    /**
     * 解析同步渲染当前输入的素材状态，并将其记录为最新输入。
     *
     * @param input - 当前 Text Render Input。
     * @returns 当前素材解析结果。
     */
    private resolve(input: TextRenderInput): AssetLoadState {
        const requests = collectAssetRequests(input);
        const hasLoading = this.hasActiveLoading();

        this.activeKeys = new Set(requests.map(createAssetKey));

        // 用于记录当前输入是否仍有素材处于 loading。
        let pending = false;

        // 用于记录当前输入是否存在加载失败的素材。
        let failure: Error | null = null;

        requests.forEach((request) => {
            const entry = this.getOrCreateEntry(request);

            // 若 entry 处于 loading 状态，则设置 pending 为 true。
            if (entry.status === 'loading') pending = true;

            // 若 entry 处于 failed 状态，则设置 failure 为 entry.error。
            if (entry.status === 'failed') {
                failure ??= entry.error ?? new Error(`Failed to load ${request.kind} asset.`);
            }
        });

        if (!hasLoading && pending) this.options.setLoading(true);
        if (hasLoading && !pending) this.options.setLoading(false);

        if (failure) return { status: 'failed', error: failure };
        if (pending) return { status: 'pending' };

        return { status: 'ready', assets: this.createAssetGetters() };
    }

    /**
     * 读取素材缓存；未命中时创建 loading entry 并立即发起加载。
     *
     * @param request - 从当前输入收集的素材请求。
     * @returns 该素材在当前 renderer 中唯一的缓存 entry。
     */
    private getOrCreateEntry(request: AssetRequest): AssetEntry {
        const key = createAssetKey(request);
        let entry = this.entries.get(key);
        if (!entry) {
            entry = { request, status: 'loading' };
            this.entries.set(key, entry);
            entry.promise = this.loadEntry(key, entry);
        }
        return entry;
    }

    /**
     * 为单个 entry 发起一次素材加载尝试。
     *
     * @param key - 当前素材请求的完整缓存键。
     * @param entry - 本次尝试拥有的 loading entry。
     * @returns 素材加载与状态发布完成后的 Promise。
     */
    private async loadEntry(key: string, entry: AssetEntry): Promise<void> {
        try {
            const asset = await loadAsset(entry.request);

            // dispose() 可能在加载期间清空缓存，只有缓存仍持有本次 entry 时才接收结果。
            if (this.entries.get(key) !== entry) return;

            entry.asset = asset;
            entry.status = 'ready';
        } catch (error) {
            // 失败结果也不应回写到已经释放的 entry。
            if (this.entries.get(key) !== entry) return;

            entry.error = error instanceof Error ? error : new Error(String(error));
            entry.status = 'failed';
        }

        // 该素材已不属于最新输入时，不能影响当前输入的 loading 和渲染。
        if (!this.activeKeys.has(key)) return;

        // 最新输入仍有其他素材在加载时，继续等待整批素材进入终态。
        if (this.hasActiveLoading()) return;

        // 最新输入的全部素材已进入 ready 或 failed，结束图层 loading。
        this.options.setLoading(false);
        this.options.notifyInvalidated();
    }

    /**
     * 判断最新输入是否仍有素材处于 loading。
     *
     * @returns 至少一个 active entry 仍在加载时返回 true。
     */
    private hasActiveLoading(): boolean {
        for (const key of this.activeKeys) {
            if (this.entries.get(key)?.status === 'loading') return true;
        }
        return false;
    }

    /**
     * 创建同步 render pipeline 使用的只读素材 getters。
     *
     * @returns 只读取当前 renderer ready entry 的内部 getters。
     */
    private createAssetGetters(): AssetGetters {
        const get = <T extends ReadyTextRenderAsset>(request: AssetRequest, kind: T['kind']): T | undefined => {
            const asset = this.entries.get(createAssetKey(request))?.asset;
            return asset?.kind === kind ? (asset as T) : undefined;
        };
        return {
            getTexture: (source) => get<ReadyTextRenderTexture>({ kind: 'texture', source }, 'texture'),
            getHighlight: (source) => get<ReadyTextRenderHighlight>({ kind: 'highlight', source }, 'highlight'),
            getArtText: (source) => get<ReadyTextRenderArtText>({ kind: 'art-text', source }, 'art-text'),
            getListMarker: (source) => get<ReadyTextRenderListMarker>({ kind: 'list-marker', source }, 'list-marker'),
        };
    }
}
